import { useMemo } from 'react'

import { PRODUCT_DETAILS, RESELLERS } from '@/common/utils'

import { useToolBoxTreatments } from './useToolBoxTreatments'

/**
 * Hook to get product details, with reseller branding precedence
 *
 * Returns the default product branding (Tempus Tools). Reseller brands
 * (Libellula, Striker) take precedence when the user is in a reseller context.
 *
 * @returns {Object} Product details object with name, title, logo, theme, etc.
 * @property {boolean} isReady - Whether Split.io has loaded and branding is resolved
 * @property {string} name - Product name (e.g., 'tempustools', 'libellula', 'striker')
 * @property {string} title - Display title (e.g., 'Tempus Tools', 'Libellula.QUOTATION')
 * @property {Function} theme - Theme function for styling
 * @property {string} logo - Logo path (e.g., '/tempustools-logo.svg', '/libellula-logo.png')
 * @property {string} organisationUnits - Default units ('imperial' or 'metric')
 * @property {string} selectedLanguage - Default language code
 * @property {string} organisationLocale - Default locale
 * @property {string} organisationCurrencyCode - Default currency code
 * @property {boolean} hasWebStoreAccess - Whether web store is available
 *
 * @example
 * const MyComponent = () => {
 *   const productDetails = useProductDetails()
 *   if (!productDetails.isReady) return null // Wait for branding to resolve
 *   return <img src={productDetails.logo} alt={productDetails.title} />
 * }
 */
export function useProductDetails() {
    const { isReady, previewResellerName } = useToolBoxTreatments()

    return useMemo(() => {
        if (previewResellerName) {
            return { ...RESELLERS[previewResellerName], isReady }
        }

        return { ...PRODUCT_DETAILS, isReady }
    }, [isReady, previewResellerName])
}
